import React, { useState, useEffect, useRef } from 'react';
import { ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListsManager, { MessageSource } from '../services/ListsManager';
import { sentimentToPct, pctToSentiment, sentimentDotColor, EMOTION_COLORS } from '../lib/utils';
import Modal from '../components/Modal';
import ReactionModal from '../components/ReactionModal';
import ListUsersScreen from './ListUsersScreen';
import NotificationSettingsScreen from './NotificationSettingsScreen';
import NewListScreen from './NewListScreen';
import FilterDropdown from '../components/FilterDropdown';
import ShareModal from '../components/ShareModal';
import {
  FaUsers, FaBell, FaEdit, FaFilter, FaShare, FaTrash, FaDownload,
  FaTimes, FaSync, FaGlobe, FaTwitter, FaTelegram, FaTiktok, FaInstagram,
  FaNewspaper, FaCommentAlt, FaExternalLinkAlt, FaExchangeAlt, FaFacebookF, FaTv,
  FaCog, FaLanguage, FaUndo, FaMagic, FaBinoculars, FaSearch, FaTag,
  FaEye, FaHeart, FaRetweet as FaRepeat, FaArrowUp, FaArrowDown, FaArrowLeft, FaSortAmountDown, FaSortAmountUp,
} from 'react-icons/fa';
import { useOrg } from '../hooks/useOrg';
import { useTheme } from '../theme/ThemeContext';

let T = {};

const PLATFORM_ICONS = {
  twitter: FaTwitter, telegram: FaTelegram,
  tiktok: FaTiktok, instagram: FaInstagram,
  facebook_group: FaFacebookF,
  rss: FaNewspaper, news: FaNewspaper,
  tv: FaTv,
};
const PLATFORM_COLORS = {
  twitter: '#1da1f2', telegram: '#0088cc',
  tiktok: '#ff0050', instagram: '#e1306c',
  facebook_group: '#1877F2',
  rss: '#f97316', news: '#6366f1',
  tv: '#9f1239',
};

function formatStat(n) {
  if (!n && n !== 0) return null;
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
}

function buildPostUrl(item) {
  if (item.url) return item.url;
  if (item.source === 'twitter' && item.message_id && item.username)
    return `https://twitter.com/${item.username}/status/${item.message_id}`;
  if (item.source === 'tiktok' && item.username && item.message_id)
    return `https://www.tiktok.com/@${item.username}/video/${item.message_id}`;
  if (item.source === 'instagram' && item.username)
    return `https://www.instagram.com/${item.username}/`;
  if (item.source === 'facebook_group' && item.message_id)
    return `https://www.facebook.com/${item.message_id}`;
  return null;
}

// ─── Unified Post Tile ─────────────────────────────────────────────────────────
function PostTile({ item, onReaction, listName, listId, isSelected, onSelect, highlightText }) {
  const platform = (item.source || 'twitter').toLowerCase();
  const PlatformIcon = PLATFORM_ICONS[platform] || FaGlobe;
  const platformColor = PLATFORM_COLORS[platform] || '#8e8e93';

  const handle = item.username || '';
  const displayName = item.author_name || handle || 'Unknown';
  const content = item.tweet_content || item.content || item.translated_content || '';
  const avatarUrl = item.author_profile_image_url;
  const initial = (displayName)[0]?.toUpperCase() || '?';
  const images = item.tweet_images || [];

  const views = formatStat(item.view_count);
  const likes = formatStat(item.like_count || item.likes);

  const relativeTime = item.created_at ? (() => {
    const diff = Date.now() - new Date(item.created_at);
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d`;
    return new Date(item.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' });
  })() : '';

  return (
    <div
      className={`ldp-post-tile${isSelected ? ' ldp-selected' : ''}`}
      onClick={() => onSelect?.(item)}
      style={{
        padding: '20px 16px',
        borderBottom: `1px solid ${T.border}`,
        cursor: 'pointer',
        background: isSelected ? `${T.primary}08` : 'transparent',
        borderLeft: isSelected ? `2px solid ${T.primary}` : '2px solid transparent',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
      }}
    >
      {/* Avatar */}
      {avatarUrl ? (
        <img src={avatarUrl} alt="" style={{
          width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
        }} onError={(e) => { e.target.style.display = 'none'; }} />
      ) : (
        <div style={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          background: `${platformColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: platformColor, fontSize: 12, fontWeight: 700,
        }}>{initial}</div>
      )}

      {/* Text content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Author row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
          <span style={{ color: T.text, fontSize: 12, fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {displayName}
          </span>
          {handle && handle !== displayName && (
            <span style={{ color: T.textMuted, fontSize: 11, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              @{handle}
            </span>
          )}
          <PlatformIcon size={10} color={platformColor} style={{ flexShrink: 0 }} />
        </div>

        {/* Content - 3 line clamp */}
        {content && (
          <div style={{
            color: T.textSec, fontSize: 12, lineHeight: '18px',
            overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
            marginBottom: 8,
          }}>
            {highlightText ? (() => {
              const escaped = highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const parts = content.split(new RegExp(`(${escaped})`, 'gi'));
              return parts.map((part, i) =>
                part.toLowerCase() === highlightText.toLowerCase()
                  ? <mark key={i} style={{ background: 'rgba(250,204,21,0.35)', color: 'inherit', borderRadius: 2, padding: '0 1px' }}>{part}</mark>
                  : part
              );
            })() : content}
          </div>
        )}

        {/* Image thumbnail */}
        {images.length > 0 && (
          <img src={images[0]} alt="" style={{
            maxHeight: 48, maxWidth: 80, borderRadius: 6, objectFit: 'cover',
            marginBottom: 6, display: 'block',
          }} onError={(e) => { e.target.style.display = 'none'; }} />
        )}

        {/* Footer: time + stats */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: T.textMuted, fontSize: 10 }}>{relativeTime}</span>
          {likes && (
            <span style={{ color: T.textMuted, fontSize: 10, display: 'flex', alignItems: 'center', gap: 2 }}>
              <FaHeart size={8} color="#ff2d55" /> {likes}
            </span>
          )}
          {views && (
            <span style={{ color: T.textMuted, fontSize: 10, display: 'flex', alignItems: 'center', gap: 2 }}>
              <FaEye size={8} color="#8e8e93" /> {views}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Settings Action Button ────────────────────────────────────────────────────
function SettingsActionBtn({ icon: Icon, label, onClick, danger }) {
  return (
    <div
      className="ldp-action-btn"
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        padding: '12px 10px',
        borderRadius: 12,
        background: danger ? 'rgba(244,63,94,0.08)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${danger ? 'rgba(244,63,94,0.2)' : T.border}`,
        cursor: 'pointer', flex: 1, minWidth: 52,
      }}
    >
      <Icon size={15} color={danger ? '#f43f5e' : T.textSec} />
      <span style={{ color: danger ? '#f43f5e' : T.textSec, fontSize: 10, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

// ─── Article Comments Panel ────────────────────────────────────────────────────
// Renders reader comments harvested for a news article, threaded by parent_id.
function ArticleCommentsPanel({ comments, loading, total, t }) {
  const isHe = (s) => /[֐-׿]/.test(s || '');
  // Sort + emotion filter run client-side on the fully-loaded comment list.
  const [sortKey, setSortKey] = useState('popularity');  // 'popularity' | 'date' | 'sentiment'
  const [sortAsc, setSortAsc] = useState(false);
  const [activeEmotions, setActiveEmotions] = useState([]);

  if (loading && comments == null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 56 }}>
        <ActivityIndicator size="small" color={T.primary} />
      </div>
    );
  }

  const list = comments || [];

  if (!list.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '64px 24px', gap: 12 }}>
        <FaCommentAlt size={26} color={T.textMuted} style={{ opacity: 0.35 }} />
        <span style={{ color: T.textMuted, fontSize: 13 }}>{t('app.lists.noComments') || 'No comments yet'}</span>
      </div>
    );
  }

  // Emotions actually present in this article's comments → the filter chips.
  const emotionsPresent = [...new Set(list.map(c => c.dominant_emotion).filter(Boolean))];

  // Emotion filter (orphaned replies promote to top-level in the threading rebuild below).
  const filtered = activeEmotions.length
    ? list.filter(c => activeEmotions.includes(c.dominant_emotion))
    : list;

  // Client-side sort: popularity=likes, date=row id (insertion order), sentiment=content score.
  const sortVal = (c) => sortKey === 'sentiment' ? (c.content_sentiment_score ?? 0)
    : sortKey === 'date' ? (c.id ?? 0)
    : (c.likes ?? 0);
  const cmp = (a, b) => { const d = sortVal(a) - sortVal(b); return sortAsc ? d : -d; };

  const known = new Set(filtered.map(c => c.comment_id));
  const repliesByParent = {};
  const topLevel = [];
  filtered.forEach(c => {
    const parent = c.is_reply && c.parent_id && known.has(c.parent_id) ? c.parent_id : null;
    if (parent) (repliesByParent[parent] = repliesByParent[parent] || []).push(c);
    else topLevel.push(c);
  });
  topLevel.sort(cmp);
  Object.values(repliesByParent).forEach(arr => arr.sort(cmp));

  const renderCard = (c) => {
    const rtl = isHe(c.text) || isHe(c.author);
    const name = c.author || (t('app.lists.anonymous') || 'Anonymous');
    const up = c.likes, down = c.dislikes;
    const csColor = sentimentDotColor(c.content_sentiment_score);
    const emotion = c.dominant_emotion;
    const brandPct = sentimentToPct(c.sentiment_score);          // brand impact, usually 0
    const showFooter = !!up || !!down || csColor != null
      || (emotion && emotion !== 'neutral') || !!brandPct;
    return (
      <div key={c.comment_id} style={{
        borderRadius: 12, border: `1px solid ${T.border}`, background: T.card,
        padding: '11px 13px', marginBottom: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
          <div style={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
            background: `${T.primary}1a`, color: T.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700,
          }}>{name[0]?.toUpperCase() || '?'}</div>
          <span style={{ color: T.text, fontSize: 12.5, fontWeight: 700, flex: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          {c.date && <span style={{ color: T.textMuted, fontSize: 10.5, flexShrink: 0 }}>{c.date}</span>}
        </div>
        <div style={{
          color: T.textSec, fontSize: 13.5, lineHeight: '20px',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          direction: rtl ? 'rtl' : 'ltr', textAlign: rtl ? 'right' : 'left',
        }}>{c.text}</div>
        {showFooter && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 9, flexWrap: 'wrap' }}>
            {!!up && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#30d158', fontSize: 11, fontWeight: 600 }}>
                <FaArrowUp size={9} /> {formatStat(up)}
              </span>
            )}
            {!!down && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#ff453a', fontSize: 11, fontWeight: 600 }}>
                <FaArrowDown size={9} /> {formatStat(down)}
              </span>
            )}
            {/* Content sentiment: colored dot (matches post-tile thresholds) */}
            {csColor != null && (
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: csColor, flexShrink: 0 }} />
            )}
            {/* Dominant emotion chip */}
            {emotion && emotion !== 'neutral' && (
              <span style={{ fontSize: 9.5, fontWeight: 600, color: EMOTION_COLORS[emotion] || '#8e8e93', textTransform: 'capitalize' }}>
                {emotion}
              </span>
            )}
            {/* Brand impact — only when it actually moved our brand */}
            {!!brandPct && (
              <span style={{ fontSize: 9.5, fontWeight: 700, color: sentimentDotColor(c.sentiment_score) || '#8e8e93' }}>
                {(t('app.lists.brandSentiment') || 'Brand')} {brandPct > 0 ? '+' : ''}{brandPct}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Header: total + sort control (Popularity / Newest / Sentiment, click active to flip dir) */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
        <span style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>
          {t('app.lists.commentsCount', { count: total }) || `${total} comments`}
        </span>
        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 3,
          background: T.surface, borderRadius: 8, padding: 3 }}>
          {[['popularity', t('app.lists.sortPopularity') || 'Popularity'],
            ['date', t('app.lists.sortNewest') || 'Newest'],
            ['sentiment', t('app.lists.sortSentiment') || 'Sentiment']].map(([key, label]) => {
            const active = sortKey === key;
            return (
              <div key={key}
                onClick={() => { if (active) setSortAsc(p => !p); else { setSortKey(key); setSortAsc(false); } }}
                style={{
                  padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4,
                  background: active ? T.primary : 'transparent',
                  color: active ? '#fff' : T.textSec, transition: 'background 0.12s ease',
                }}>
                {label}{active && (sortAsc ? <FaSortAmountUp size={9} /> : <FaSortAmountDown size={9} />)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Emotion filter chips (only emotions present in this article's comments) */}
      {emotionsPresent.length > 1 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 14 }}>
          {emotionsPresent.map(e => {
            const active = activeEmotions.includes(e);
            const color = EMOTION_COLORS[e] || '#8e8e93';
            return (
              <div key={e}
                onClick={() => setActiveEmotions(prev => active ? prev.filter(x => x !== e) : [...prev, e])}
                style={{
                  padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                  textTransform: 'capitalize', border: `1px solid ${active ? color : T.border}`,
                  background: active ? `${color}15` : 'transparent', color: active ? color : T.textSec,
                }}>{e}</div>
            );
          })}
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 12px' }}>
          <ActivityIndicator size="small" color={T.primary} />
        </div>
      )}

      {!topLevel.length && (
        <div style={{ padding: '32px 24px', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
          {t('app.lists.noCommentsMatch') || 'No comments match this filter'}
        </div>
      )}

      {topLevel.map(c => (
        <div key={c.comment_id}>
          {renderCard(c)}
          {(repliesByParent[c.comment_id] || []).length > 0 && (
            <div style={{ marginInlineStart: 16, borderInlineStart: `2px solid ${T.border}`, paddingInlineStart: 12 }}>
              {repliesByParent[c.comment_id].map(r => renderCard(r))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Post Detail Panel (Right Column) ──────────────────────────────────────────
function PostDetailView({ post, listId, listName, onClose, onDraftReaction, onRemovePost, t, highlightText }) {
  const { activeOrg } = useOrg();
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState(null);
  const [isTranslated, setIsTranslated] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [isTracked, setIsTracked] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [orgLists, setOrgLists] = useState([]);
  const [isMoving, setIsMoving] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [editingReason, setEditingReason] = useState(false);
  const [editingSentiment, setEditingSentiment] = useState(false);
  const [summaryValue, setSummaryValue] = useState('');
  const [reasonValue, setReasonValue] = useState('');
  const [sentimentValue, setSentimentValue] = useState('');
  const [savingField, setSavingField] = useState(null);
  const moveMenuRef = useRef(null);
  const scrollRef = useRef(null);

  // Comments tab state
  const [activeTab, setActiveTab] = useState('post');        // 'post' | 'comments'
  const [articleComments, setArticleComments] = useState(null);  // null = not loaded yet
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsTotal, setCommentsTotal] = useState(null);

  useEffect(() => {
    setIsTranslated(false);
    setTranslatedContent(null);
    setIsTracked(false);
    setShowMoveMenu(false);
    setEditingSummary(false);
    setEditingReason(false);
    setEditingSentiment(false);
    setSummaryValue(post?.summary || '');
    setReasonValue(post?.reason || '');
    setActiveTab('post');
    setArticleComments(null);
    setCommentsTotal(null);
  }, [post?.tweet_id || post?.id]);

  // Lazy-load comments when the Comments tab is active (news/webz posts only).
  useEffect(() => {
    if (activeTab !== 'comments' || !post) return;
    const src = (post.source || '').toLowerCase();
    if ((src !== 'webz' && src !== 'news') || !post.url) return;
    let cancelled = false;
    setCommentsLoading(true);
    // Load the full set once; sorting + emotion filtering happen client-side in the panel.
    ListsManager.fetchArticleComments(post.url, { orgId: activeOrg?.id, sort: 'top', limit: 500 })
      .then(res => {
        if (cancelled) return;
        setArticleComments(res?.comments || []);
        setCommentsTotal(res?.total ?? (res?.comments?.length || 0));
      })
      .finally(() => { if (!cancelled) setCommentsLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, post?.url, activeOrg?.id]);

  useEffect(() => {
    if (!showMoveMenu) return;
    const handleClickOutside = (e) => {
      if (moveMenuRef.current && !moveMenuRef.current.contains(e.target)) setShowMoveMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoveMenu]);

  if (!post) return null;

  const url = buildPostUrl(post);
  const platform = (post.source || 'twitter').toLowerCase();
  const PlatformIcon = PLATFORM_ICONS[platform] || FaGlobe;
  const platformColor = PLATFORM_COLORS[platform] || '#8e8e93';
  const handle = post.username || post.author_name || '';
  const displayName = post.author_name || handle || 'Unknown';
  const rawContent = post.tweet_content || post.content || '';
  const translated = post.translated_content || '';
  const content = isTranslated && translatedContent ? translatedContent
    : isTranslated && translated ? translated
    : rawContent || translated;
  const images = post.tweet_images || [];
  const avatarUrl = post.author_profile_image_url;
  const initial = displayName[0]?.toUpperCase() || '?';
  const isVerified = post.verified_type && ['blue', 'business', 'government'].includes(post.verified_type);

  const views = formatStat(post.view_count);
  const likes = formatStat(post.like_count || post.likes);
  const comments = formatStat(post.comment_count || post.reply_count || post.comments);
  const reposts = formatStat(post.retweet_count);
  const shares = formatStat(post.share_count);

  const sentimentPct = sentimentToPct(post.sentiment_score);
  const sentimentColor = (pct) => {
    if (pct == null) return '#8e8e93';
    if (pct <= -60) return '#d32f2f';
    if (pct <= -20) return '#ff453a';
    if (pct < 20) return '#8e8e93';
    if (pct < 60) return '#30d158';
    return '#1b8a2e';
  };

  const renderHighlight = (text) => {
    if (!highlightText || !text) return text;
    const escaped = highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    if (parts.length === 1) return text;
    return parts.map((part, i) =>
      part.toLowerCase() === highlightText.toLowerCase()
        ? <mark key={i} style={{ background: 'rgba(250,204,21,0.35)', color: 'inherit', borderRadius: 2, padding: '0 1px' }}>{part}</mark>
        : part
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  const handleTranslate = async () => {
    if (isTranslated) {
      setIsTranslated(false);
      return;
    }
    if (translatedContent) {
      setIsTranslated(true);
      return;
    }
    const textToTranslate = rawContent || translated;
    if (!textToTranslate) return;
    setIsTranslating(true);
    try {
      const lang = await ListsManager.getListenerLanguage();
      const result = await ListsManager.translateTweet(textToTranslate, lang || 'en');
      if (result && result !== textToTranslate) {
        setTranslatedContent(result);
        setIsTranslated(true);
      }
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleTrack = async () => {
    if (isTracked) return;
    setIsTracking(true);
    try {
      const pf = platform === 'telegram' ? 'telegram' : platform === 'tiktok' ? 'tiktok'
        : platform === 'instagram' ? 'instagram' : platform === 'facebook_group' ? 'facebook_group'
        : platform === 'rss' ? 'rss' : platform === 'tv' ? 'tv' : 'twitter';
      const body = {
        tracker_type: 'saved_content',
        title: `${displayName}: ${(rawContent || translated || '').slice(0, 60)}`,
        source_post_id: String(post.tweet_id || post.id),
        source_platform: pf,
        source_url: post.url || null,
        schedule_pattern: 'passive',
      };
      if (activeOrg?.id) body.org_id = activeOrg.id;
      if (listId) body.list_id = listId;
      const result = await ListsManager.createTracker(body);
      if (result) setIsTracked(true);
    } catch (err) {
      console.error('Track failed:', err);
    } finally {
      setIsTracking(false);
    }
  };

  const handleRemove = () => {
    if (!window.confirm(t('app.tweetCell.removeConfirm'))) return;
    if (onRemovePost) onRemovePost(post);
    ListsManager.removeMessage(post.tweet_id || post.id, post.source || 'twitter').catch(console.error);
    onClose();
  };

  const handleSaveField = async (field) => {
    let value;
    if (field === 'summary') value = summaryValue;
    else if (field === 'reason') value = reasonValue;
    else if (field === 'sentiment_score') value = pctToSentiment(sentimentValue);
    setSavingField(field);
    try {
      const success = await ListsManager.updateMessageFields(
        post.tweet_id || post.id,
        post.source || 'twitter',
        { [field]: value }
      );
      if (success) {
        post[field] = value;
        if (field === 'summary') setEditingSummary(false);
        else if (field === 'reason') setEditingReason(false);
        else if (field === 'sentiment_score') setEditingSentiment(false);
      }
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSavingField(null);
    }
  };

  const handleShare = () => {
    if (navigator.share && url) {
      navigator.share({ url }).catch(() => {});
    } else if (url) {
      navigator.clipboard.writeText(url);
    }
  };

  const handleOpenMoveMenu = async () => {
    if (showMoveMenu) { setShowMoveMenu(false); return; }
    try {
      const lists = await ListsManager.fetchLists(activeOrg?.id);
      setOrgLists((lists || []).filter(l => l.id !== listId));
      setShowMoveMenu(true);
    } catch (err) {
      console.error('Failed to fetch lists:', err);
    }
  };

  const handleMoveToList = async (targetListId) => {
    setIsMoving(true);
    try {
      const success = await ListsManager.movePostToList(
        post.tweet_id || post.id,
        post.source || 'twitter',
        targetListId
      );
      if (success) {
        setShowMoveMenu(false);
        if (onRemovePost) onRemovePost(post);
        onClose();
      }
    } catch (err) {
      console.error('Move failed:', err);
    } finally {
      setIsMoving(false);
    }
  };

  const tiktokVideoId = platform === 'tiktok' && post.url
    ? (post.url.match(/video\/(\d+)/) || [])[1] : null;

  const DetailActionBtn = ({ icon: Icon, label, onClick, accent, danger, disabled, loading: isLoading }) => (
    <button
      className="ldp-detail-action"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 12px', borderRadius: 10,
        background: danger ? 'rgba(244,63,94,0.08)' : accent ? `${T.primary}12` : T.surface,
        border: `1px solid ${danger ? 'rgba(244,63,94,0.2)' : accent ? `${T.primary}30` : T.border}`,
        cursor: disabled ? 'default' : 'pointer',
        color: danger ? '#f43f5e' : accent ? T.primary : T.textSec,
        fontSize: 12, fontWeight: 600,
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {isLoading ? <ActivityIndicator size="small" color={T.primary} /> : <Icon size={12} />}
      {label}
    </button>
  );

  const STAT_CONFIG = [
    { key: 'views',    value: views,    icon: FaEye,        color: '#8e8e93' },
    { key: 'likes',    value: likes,    icon: FaHeart,      color: '#ff2d55' },
    { key: 'comments', value: comments, icon: FaCommentAlt, color: '#5ac8fa' },
    { key: 'reposts',  value: reposts,  icon: FaRepeat,     color: '#30d158' },
    { key: 'shares',   value: shares,   icon: FaArrowUp,    color: '#af52de' },
  ].filter(s => s.value);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header bar with tabs */}
      <div style={{
        padding: '0 28px', borderBottom: `1px solid ${T.border}`,
        flexShrink: 0, display: 'flex', alignItems: 'stretch', gap: 0,
      }}>
        {/* Tab: Post */}
        <div
          onClick={() => setActiveTab('post')}
          style={{
            padding: '14px 16px 12px', cursor: 'pointer',
            borderBottom: `2px solid ${activeTab === 'post' ? T.text : 'transparent'}`,
            color: activeTab === 'post' ? T.text : T.textMuted,
            fontSize: 13, fontWeight: activeTab === 'post' ? 600 : 500,
            transition: 'color 0.12s ease',
          }}>
          {t('app.lists.postTab') || 'Post'}
        </div>
        {/* Tab: Comments — news/webz articles only */}
        {(platform === 'webz' || platform === 'news') && (
          <div
            onClick={() => setActiveTab('comments')}
            style={{
              padding: '14px 16px 12px', cursor: 'pointer',
              borderBottom: `2px solid ${activeTab === 'comments' ? T.text : 'transparent'}`,
              color: activeTab === 'comments' ? T.text : T.textMuted,
              fontSize: 13, fontWeight: activeTab === 'comments' ? 600 : 500,
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'color 0.12s ease',
            }}>
            {t('app.lists.commentsTab') || 'Comments'}
            {commentsTotal != null && commentsTotal > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: T.primary,
                background: `${T.primary}1a`, borderRadius: 10, padding: '1px 7px',
              }}>{commentsTotal}</span>
            )}
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Close */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            onClick={onClose}
            style={{ cursor: 'pointer', padding: 6, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <FaTimes size={13} color={T.textMuted} />
          </div>
        </div>
      </div>

      {/* Content area: Comments tab takes the full width; Post tab is the split */}
      {activeTab === 'comments' ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px 40px' }}>
          <ArticleCommentsPanel
            comments={articleComments}
            loading={commentsLoading}
            total={commentsTotal ?? 0}
            t={t}
          />
        </div>
      ) : (
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left column: post details */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '28px 36px 40px', minWidth: 0 }}>

        {/* ── Title (post summary or content excerpt) ── */}
        <h1 style={{ color: T.text, fontSize: 22, fontWeight: 700, lineHeight: '30px', margin: '0 0 16px' }}>
          {post.summary || displayName}
        </h1>

        {/* ── Metadata row — labeled like reference ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24,
          fontSize: 13, color: T.textMuted, flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 600, color: T.textSec }}>{t('app.lists.network') || 'Network'}</span>
          <span style={{ margin: '0 6px' }}>
            <PlatformIcon size={12} color={platformColor} />
          </span>
          <span style={{ color: platformColor, fontWeight: 600 }}>
            {platform === 'facebook_group' ? 'Facebook' : platform.charAt(0).toUpperCase() + platform.slice(1)}
          </span>
          <span style={{ margin: '0 10px', color: T.border }}>|</span>

          <span style={{ fontWeight: 600, color: T.textSec }}>{t('app.lists.datePosted') || 'Date posted'}</span>
          <span style={{ margin: '0 6px' }}>{formatDate(post.created_at)}</span>
          <span style={{ margin: '0 10px', color: T.border }}>|</span>

          <span style={{ fontWeight: 600, color: T.textSec }}>{t('app.lists.brandSentiment') || 'Brand Sentiment'}</span>
          {editingSentiment ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: '0 6px' }}>
              <input
                type="number"
                min={-100}
                max={100}
                value={sentimentValue}
                autoFocus
                onChange={e => setSentimentValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveField('sentiment_score'); if (e.key === 'Escape') setEditingSentiment(false); }}
                style={{ width: 56, fontSize: 13, fontWeight: 700, color: T.text, background: T.surface,
                  border: `1px solid ${T.border}`, borderRadius: 6, padding: '2px 6px', fontFamily: 'inherit' }}
              />
              <button onClick={() => handleSaveField('sentiment_score')} disabled={savingField === 'sentiment_score'}
                style={{ fontSize: 11, fontWeight: 600, color: 'white', background: T.primary,
                  border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>
                {savingField === 'sentiment_score' ? '...' : 'Save'}
              </button>
              <button onClick={() => setEditingSentiment(false)}
                style={{ fontSize: 11, color: T.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>
                Cancel
              </button>
            </span>
          ) : (
            <>
              <span style={{ margin: '0 6px', fontWeight: 700, color: sentimentColor(sentimentPct) }}>
                {sentimentPct == null ? '—' : `${sentimentPct > 0 ? '+' : ''}${sentimentPct}`}
              </span>
              <button onClick={() => {
                setSentimentValue(String(sentimentPct ?? 0));
                setEditingSentiment(true);
              }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                <FaEdit size={11} color={T.textMuted} />
              </button>
            </>
          )}
          {post.content_sentiment_score != null && (() => {
            const contentPct = sentimentToPct(post.content_sentiment_score);
            return (
              <>
                <span style={{ margin: '0 10px', color: T.border }}>|</span>
                <span style={{ fontWeight: 600, color: T.textSec }}>{t('app.lists.contentSentiment') || 'Content Sentiment'}</span>
                <span style={{ margin: '0 6px', fontWeight: 700, color: sentimentColor(contentPct) }}>
                  {contentPct == null ? '—' : `${contentPct > 0 ? '+' : ''}${contentPct}`}
                </span>
              </>
            );
          })()}
        </div>

        {/* Lists this post belongs to */}
        {post._all_lists && post._all_lists.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            <FaTag size={11} color={T.textMuted} />
            {post._all_lists.map((l, i) => (
              <span key={i} style={{
                fontSize: 11, fontWeight: 600, color: T.primary, background: `${T.primary}10`,
                padding: '2px 8px', borderRadius: 6,
              }}>{l.name}</span>
            ))}
          </div>
        )}

        {/* Translation indicator */}
        {isTranslated && (
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 5 }}>
            <FaLanguage size={13} color={T.primary} />
            <span style={{ color: T.primary, fontSize: 11 }}>{t('app.tweetCell.translatedFromOriginal')}</span>
          </div>
        )}

        {/* Summary */}
        {(post.summary || editingSummary) && content && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 8px' }}>
              <h3 style={{ color: T.text, fontSize: 14, fontWeight: 700, margin: 0 }}>
                {t('app.lists.summary')}
              </h3>
              {!editingSummary && (
                <button onClick={() => { setSummaryValue(post.summary || ''); setEditingSummary(true); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                  <FaEdit size={12} color={T.textMuted} />
                </button>
              )}
            </div>
            {editingSummary ? (
              <div>
                <textarea value={summaryValue} onChange={e => setSummaryValue(e.target.value)}
                  style={{ width: '100%', fontSize: 13, lineHeight: '21px', color: T.text, background: T.surface,
                    border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 10px', resize: 'vertical',
                    minHeight: 60, fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <button onClick={() => handleSaveField('summary')} disabled={savingField === 'summary'}
                    style={{ fontSize: 12, fontWeight: 600, color: 'white', background: T.primary,
                      border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
                    {savingField === 'summary' ? '...' : 'Save'}
                  </button>
                  <button onClick={() => setEditingSummary(false)}
                    style={{ fontSize: 12, color: T.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ color: T.textSec, fontSize: 13, lineHeight: '21px' }}>{post.summary}</div>
            )}
          </div>
        )}

        {/* Reason / Why Included */}
        {(post.reason || editingReason) && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 8px' }}>
              <h3 style={{ color: T.text, fontSize: 14, fontWeight: 700, margin: 0 }}>
                {t('app.lists.whyIncluded')}
              </h3>
              {!editingReason && (
                <button onClick={() => { setReasonValue(post.reason || ''); setEditingReason(true); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                  <FaEdit size={12} color={T.textMuted} />
                </button>
              )}
            </div>
            {editingReason ? (
              <div>
                <textarea value={reasonValue} onChange={e => setReasonValue(e.target.value)}
                  style={{ width: '100%', fontSize: 13, lineHeight: '21px', color: T.text, background: T.surface,
                    border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 10px', resize: 'vertical',
                    minHeight: 60, fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <button onClick={() => handleSaveField('reason')} disabled={savingField === 'reason'}
                    style={{ fontSize: 12, fontWeight: 600, color: 'white', background: T.primary,
                      border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
                    {savingField === 'reason' ? '...' : 'Save'}
                  </button>
                  <button onClick={() => setEditingReason(false)}
                    style={{ fontSize: 12, color: T.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ color: T.textSec, fontSize: 13, lineHeight: '21px' }}>{post.reason}</div>
            )}
          </div>
        )}

        {/* Dependencies as embedded tweets (reply / quote parents) */}
        {post.dependencies && post.dependencies.length > 0 && (
          <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {post.dependencies.map((dep, i) => {
              // New structured shape from the backend:
              //   { relation, relation_label, username, name, profile_image_url, content, url }
              // Legacy fallback: raw string 'Dependency N (replied_to), posted by @user: "..."'
              let depType = '', depUser = '', depName = '', depContent = '', depAvatar = '', depUrl = '';
              if (typeof dep === 'object' && dep !== null) {
                depType = dep.relation || dep.type || dep.dependency_type || '';
                depUser = dep.username || dep.author || '';
                depName = dep.name || dep.author_name || depUser;
                depContent = dep.content || dep.tweet_content || dep.text || '';
                depAvatar = dep.profile_image_url || '';
                depUrl = dep.url || '';
              } else if (typeof dep === 'string') {
                const typeMatch = dep.match(/^\s*(?:Dependency\s*\d*\s*)?[\(\[]?(replied_to|part_of_conversation|retweet|retweeted|quote|quoted|mention|mentioned)[\)\]]?\s*,?\s*/i);
                if (typeMatch) {
                  depType = typeMatch[1];
                  const rest = dep.slice(typeMatch[0].length);
                  const userMatch = rest.match(/^(?:posted\s+by\s+|by\s+)?@?(\w+)\s*[:\-–—]\s*/i);
                  if (userMatch) {
                    depUser = userMatch[1];
                    depContent = rest.slice(userMatch[0].length).trim().replace(/^"|"$/g, '');
                  } else {
                    depContent = rest.trim();
                  }
                } else {
                  depContent = dep;
                }
                depName = depUser;
              }

              const REL_LABELS = {
                replied_to: t('app.lists.replyTo') || 'Reply to',
                part_of_conversation: t('app.lists.partOfConversation') || 'In conversation with',
                quoted: t('app.lists.quoted') || 'Quoting',
                quote: t('app.lists.quoted') || 'Quoting',
                retweeted: t('app.lists.retweeted') || 'Retweet of',
                retweet: t('app.lists.retweeted') || 'Retweet of',
              };
              const relKey = (depType || '').toLowerCase();
              const typeLabel = dep.relation_label || REL_LABELS[relKey] ||
                (depType ? depType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '');
              const TypeIcon = relKey.includes('retweet') || relKey.includes('quote') ? FaRepeat : FaCommentAlt;
              const isRtl = /[֐-׿؀-ۿ]/.test(depContent);

              return (
                <div key={i} style={{
                  borderRadius: 14, border: `1px solid ${T.border}`,
                  background: T.card, overflow: 'hidden',
                  padding: '12px 14px',
                }}>
                  {/* Relation badge */}
                  {(typeLabel || depUser) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8, color: T.textMuted, fontSize: 11 }}>
                      <TypeIcon size={10} color="#1da1f2" style={{ transform: 'scaleX(-1)' }} />
                      <span style={{ color: '#1da1f2', fontWeight: 600 }}>
                        {typeLabel}{depUser ? ` @${depUser}` : ''}
                      </span>
                    </div>
                  )}
                  {/* Author row */}
                  {depUser && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      {depAvatar ? (
                        <img src={depAvatar} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%', background: 'rgba(29,161,242,0.1)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <span style={{ color: '#1da1f2', fontSize: 12, fontWeight: 700 }}>{(depName || depUser)[0]?.toUpperCase() || '?'}</span>
                        </div>
                      )}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ color: T.text, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{depName || depUser}</div>
                        <div style={{ color: T.textMuted, fontSize: 11 }}>@{depUser}</div>
                      </div>
                      <FaTwitter size={14} color="#1da1f2" style={{ opacity: 0.6, flexShrink: 0 }} />
                    </div>
                  )}
                  {/* Content */}
                  {depContent && (
                    <div style={{
                      color: T.textSec, fontSize: 14, lineHeight: '21px',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left',
                    }}>
                      {depContent}
                    </div>
                  )}
                  {/* Open original tweet */}
                  {depUrl && (
                    <a href={depUrl} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 10,
                        padding: '5px 12px', borderRadius: 999, background: '#0f1419', color: '#fff',
                        fontSize: 11, fontWeight: 700, textDecoration: 'none',
                      }}>
                      <FaTwitter size={10} /> {t('app.lists.openOriginalTweet') || 'Open original tweet'}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Stats bar ── */}
        {STAT_CONFIG.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 24,
            padding: '14px 0', borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
            marginBottom: 20,
          }}>
            {STAT_CONFIG.map(stat => {
              const StatIcon = stat.icon;
              return (
                <div key={stat.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <StatIcon size={14} color={stat.color} />
                  <span style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>{stat.value}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Actions ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {url && (
            <DetailActionBtn icon={FaExternalLinkAlt} label={t('app.lists.open')}
              onClick={() => window.open(url, '_blank')} accent />
          )}
          <DetailActionBtn icon={FaShare} label={t('app.lists.share')}
            onClick={handleShare} />
          <DetailActionBtn
            icon={isTranslating ? () => <ActivityIndicator size="small" color={T.primary} /> : isTranslated ? FaUndo : FaLanguage}
            label={isTranslating ? t('app.tweetCell.translating') : isTranslated ? t('app.tweetCell.showOriginal') : t('app.tweetCell.translate')}
            onClick={handleTranslate}
            disabled={isTranslating}
          />
          <DetailActionBtn icon={FaMagic} label={t('app.lists.draftReaction')}
            onClick={() => onDraftReaction?.(post, listId)}
            accent />
          <DetailActionBtn
            icon={FaBinoculars}
            label={isTracked ? t('app.lists.tracked') : t('app.lists.track')}
            onClick={handleTrack}
            disabled={isTracking || isTracked}
            loading={isTracking}
          />
          {listId && (
            <div style={{ position: 'relative' }} ref={moveMenuRef}>
              <DetailActionBtn
                icon={FaExchangeAlt}
                label={isMoving ? t('app.lists.moving') : t('app.lists.moveToList')}
                onClick={handleOpenMoveMenu}
                disabled={isMoving}
                loading={isMoving}
              />
              {showMoveMenu && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 999,
                  background: T.card, border: `1px solid ${T.border}`, borderRadius: 10,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 200, maxHeight: 240,
                  overflowY: 'auto', padding: '6px 0',
                }}>
                  {orgLists.length === 0 ? (
                    <div style={{ padding: '10px 14px', color: T.textMuted, fontSize: 13 }}>{t('app.lists.noOtherLists')}</div>
                  ) : orgLists.map(l => (
                    <button key={l.id} onClick={() => handleMoveToList(l.id)} style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px',
                      background: 'none', border: 'none', cursor: 'pointer', fontSize: 13,
                      color: T.text, borderRadius: 0,
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = T.surface}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      {l.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ flex: 1 }} />
          <DetailActionBtn icon={FaTrash} label={t('app.lists.remove')}
            onClick={handleRemove} danger />
        </div>
      </div>

        {/* Right column: Platform embed preview */}
        <div style={{ width: 320, flexShrink: 0, borderLeft: `1px solid ${T.border}`, overflowY: 'auto', padding: '20px 16px', background: T.bg }}>
          {platform === 'twitter' ? (
            <div style={{
              borderRadius: 14, border: `1px solid ${T.border}`,
              borderLeft: `3px solid #1da1f2`, background: T.card,
              padding: '16px 18px', overflow: 'hidden',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1da1f215',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaTwitter size={14} color="#1da1f2" />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: T.text, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                  {handle && <div style={{ color: T.textMuted, fontSize: 11 }}>@{handle}</div>}
                </div>
                <FaTwitter size={16} color="#1da1f2" />
              </div>
              <div style={{ color: T.textSec, fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-wrap' }}>{content}</div>
              {images.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  {images.slice(0, 2).map((src, i) => (
                    <img key={i} src={src} alt="" style={{
                      width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 10, marginBottom: 6, cursor: 'pointer',
                    }} onClick={() => window.open(src, '_blank')}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                  ))}
                </div>
              )}
              {url && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                  <a href={url} target="_blank" rel="noopener noreferrer"
                    style={{ color: '#1da1f2', fontSize: 11, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FaExternalLinkAlt size={9} /> View on X
                  </a>
                </div>
              )}
            </div>
          ) : platform === 'telegram' ? (
            <div style={{
              borderRadius: 14, background: T.card, border: `1px solid ${T.border}`,
              padding: '16px 18px', position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: '#0088cc' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingLeft: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#0088cc20',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaTelegram size={14} color="#0088cc" />
                </div>
                <div>
                  <div style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>{displayName}</div>
                  <div style={{ color: '#0088cc', fontSize: 10, fontWeight: 600 }}>Telegram</div>
                </div>
              </div>
              <div style={{ color: T.textSec, fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-wrap', paddingLeft: 8 }}>{content}</div>
              {images.length > 0 && (
                <div style={{ marginTop: 12, paddingLeft: 8 }}>
                  {images.slice(0, 2).map((src, i) => (
                    <img key={i} src={src} alt="" style={{
                      width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 10, cursor: 'pointer', marginBottom: 6,
                    }} onClick={() => window.open(src, '_blank')}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                  ))}
                </div>
              )}
              {url && (
                <div style={{ marginTop: 10, paddingLeft: 8 }}>
                  <a href={url} target="_blank" rel="noopener noreferrer"
                    style={{ color: '#0088cc', fontSize: 11, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FaExternalLinkAlt size={9} /> Open in Telegram
                  </a>
                </div>
              )}
            </div>
          ) : platform === 'tv' && post.video_url ? (
            <div style={{ borderRadius: 14, overflow: 'hidden', background: T.card, border: `1px solid ${T.border}` }}>
              <div style={{ background: '#000' }}>
                <video src={post.video_url} controls playsInline
                  style={{ width: '100%', aspectRatio: '16/9', display: 'block', background: '#000' }} />
              </div>
              {content && (
                <div style={{ color: T.textSec, fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-wrap', padding: '14px 18px' }}>{content}</div>
              )}
            </div>
          ) : platform === 'tiktok' && tiktokVideoId ? (
            <div style={{ borderRadius: 14, overflow: 'hidden', background: '#000', border: `1px solid ${T.border}` }}>
              <iframe src={`https://www.tiktok.com/embed/v2/${tiktokVideoId}`}
                style={{ width: '100%', height: 740, border: 'none', display: 'block' }}
                allowFullScreen allow="encrypted-media"
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" />
            </div>
          ) : platform === 'instagram' && url ? (
            <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${T.border}` }}>
              {(() => {
                const shortcode = url.match(/instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/)?.[1];
                if (shortcode) {
                  return (
                    <iframe src={`https://www.instagram.com/p/${shortcode}/embed/`}
                      style={{ width: '100%', height: 480, border: 'none', display: 'block' }}
                      allowFullScreen />
                  );
                }
                return (
                  <div style={{ padding: '16px 18px', background: T.card }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <FaInstagram size={16} color="#e1306c" />
                      <span style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>{displayName}</span>
                    </div>
                    <div style={{ color: T.textSec, fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-wrap' }}>{content}</div>
                    {images.length > 0 && (
                      <img src={images[0]} alt="" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 10, marginTop: 12 }}
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#e1306c', fontSize: 11, fontWeight: 600, marginTop: 10, display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
                      <FaExternalLinkAlt size={9} /> View on Instagram
                    </a>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div style={{
              borderRadius: 14, border: `1px solid ${T.border}`,
              borderLeft: `3px solid ${platformColor}`, background: T.card,
              padding: '16px 18px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <PlatformIcon size={14} color={platformColor} />
                <span style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>{displayName}</span>
                {handle && <span style={{ color: T.textMuted, fontSize: 11 }}>@{handle}</span>}
              </div>
              <div style={{ color: T.textSec, fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-wrap' }}>{content}</div>
              {images.length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                  {images.slice(0, 4).map((src, i) => (
                    <img key={i} src={src} alt="" style={{
                      flex: '1 1 45%', maxHeight: 160, objectFit: 'cover', borderRadius: 10, cursor: 'pointer',
                    }} onClick={() => window.open(src, '_blank')}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                  ))}
                </div>
              )}
              {url && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                  <a href={url} target="_blank" rel="noopener noreferrer"
                    style={{ color: platformColor, fontSize: 11, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FaExternalLinkAlt size={9} /> View original
                  </a>
                </div>
              )}
            </div>
          )}

          {/* ── Who posted this ── */}
          {(handle || displayName) && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                {t('app.lists.whoPostedThis')}
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', borderRadius: 10,
                background: T.card, border: `1px solid ${T.border}`,
                borderLeft: `3px solid ${platformColor}`,
              }}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" style={{ width: 36, height: 36, borderRadius: 18, objectFit: 'cover', flexShrink: 0, border: `2px solid ${platformColor}40` }}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div style={{ width: 36, height: 36, borderRadius: 18, background: `${platformColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <PlatformIcon size={16} color={platformColor} />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ color: T.text, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{handle || displayName}</span>
                    <PlatformIcon size={11} color={platformColor} />
                  </div>
                  {(() => {
                    const followerVal = post.author_followers || post.post_author_followers || 0;
                    if (followerVal <= 0) return null;
                    const influenceLevel = followerVal >= 1000000 ? { label: 'Mega Influencer', color: '#bf5af2' }
                      : followerVal >= 100000 ? { label: 'Macro Influencer', color: '#ff9f0a' }
                      : followerVal >= 10000  ? { label: 'Mid-tier', color: '#0a84ff' }
                      : followerVal >= 1000   ? { label: 'Micro', color: '#30d158' }
                      : { label: 'Nano', color: '#8e8e93' };
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FaUsers size={9} color={T.textMuted} />
                        <span style={{ color: T.textSec, fontSize: 11, fontWeight: 600 }}>{formatStat(followerVal)}</span>
                        <span style={{ padding: '1px 5px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: `${influenceLevel.color}18`, color: influenceLevel.color }}>
                          {influenceLevel.label}
                        </span>
                      </div>
                    );
                  })()}
                </div>
                {url && (
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', color: platformColor, flexShrink: 0 }}>
                    <FaExternalLinkAlt size={9} />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {showShareModal && (
        <ShareModal tweet={post} onClose={() => setShowShareModal(false)} />
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
const ListDetailPanel = ({ listId, listName, onListDeleted, onListUpdated, showSettings, onCloseSettings }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { activeOrg, orgs } = useOrg();
  const { t, i18n } = useTranslation();

  T = {
    bg:        theme.bg,
    card:      theme.bgCard,
    surface:   theme.bgSurface,
    border:    theme.border,
    text:      theme.textPrimary,
    textSec:   theme.textSecondary,
    textMuted: theme.textMuted,
    primary:   '#4DA6FF',
    error:     '#ff453a',
  };

  const [listDetails, setListDetails] = useState(null);
  const [tweets, setTweets] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [leftView, setLeftView] = useState('posts');
  const [rightView, setRightView] = useState('empty');

  const [showReactionModal, setShowReactionModal] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(false);
  const [reactionText, setReactionText] = useState('');
  const [reactionExplanation, setReactionExplanation] = useState('');
  const [reactionError, setReactionError] = useState(null);
  const [currentReactionTweet, setCurrentReactionTweet] = useState(null);
  const [currentReactionListId, setCurrentReactionListId] = useState(null);

  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 40;

  const isFetchingRef = useRef(false);

  const [currentSelectedUsers, setCurrentSelectedUsers] = useState(null);
  const [currentFromDate, setCurrentFromDate] = useState(null);
  const [currentToDate, setCurrentToDate] = useState(null);
  const [currentSelectedSources, setCurrentSelectedSources] = useState([
    MessageSource.TWITTER, MessageSource.TELEGRAM,
    MessageSource.TIKTOK, MessageSource.INSTAGRAM,
    MessageSource.FACEBOOK_GROUP, MessageSource.WEBZ, 'rss', MessageSource.TV,
  ]);

  const [selectedTweets, setSelectedTweets] = useState([]);
  const [showNewContentBubble, setShowNewContentBubble] = useState(false);
  const [newContentCount, setNewContentCount] = useState(0);

  const [userLanguage, setUserLanguage] = useState('en');

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTargetOrgId, setTransferTargetOrgId] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState('');

  const [selectedPost, setSelectedPost] = useState(null);

  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [sidebarW, setSidebarW] = useState(380);
  const dragRef = useRef(null);
  const dragStartRef = useRef(null);

  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterBtnRef = useRef(null);
  const activeFiltersRef = useRef({
    sources: [
      MessageSource.TWITTER, MessageSource.TELEGRAM,
      MessageSource.TIKTOK, MessageSource.INSTAGRAM,
      MessageSource.FACEBOOK_GROUP, MessageSource.WEBZ, 'rss', MessageSource.TV,
    ],
    fromDate: null,
    toDate: null,
    selectedUsers: null,
  });

  const SORT_OPTIONS = [
    { key: 'date',       label: t('app.lists.sortDate') || 'Date',       defaultDir: 'desc' },
    { key: 'popularity', label: t('app.lists.sortPopularity') || 'Popularity', defaultDir: 'desc' },
    { key: 'risk',       label: t('app.lists.sortRisk') || 'Brand Risk', defaultDir: 'desc' },
  ];

  const applySort = (arr) => {
    return [...arr].sort((a, b) => {
      let valA, valB;
      if (sortKey === 'date') {
        valA = new Date(a.created_at || 0).getTime();
        valB = new Date(b.created_at || 0).getTime();
      } else if (sortKey === 'popularity') {
        valA = (a.like_count || a.likes || 0) + (a.view_count || 0) * 0.1 + (a.retweet_count || a.share_count || 0) * 2;
        valB = (b.like_count || b.likes || 0) + (b.view_count || 0) * 0.1 + (b.retweet_count || b.share_count || 0) * 2;
      } else {
        const risk = (x) => {
          const s = x.sentiment_score;
          const sentimentRisk = s != null ? (-(typeof s === 'number' ? s : parseFloat(s) || 0) + 1) / 2 : 0.5;
          const reachNorm = Math.min(Math.log1p(x.author_followers || x.post_author_followers || 0) / 15, 1);
          const engNorm = Math.min(Math.log1p((x.like_count || x.likes || 0) + (x.retweet_count || x.share_count || 0)) / 10, 1);
          return sentimentRisk * 0.5 + reachNorm * 0.3 + engNorm * 0.2;
        };
        valA = risk(a);
        valB = risk(b);
      }
      return sortDir === 'desc' ? valB - valA : valA - valB;
    });
  };

  const startDrag = (e) => {
    e.preventDefault();
    dragRef.current = true;
    dragStartRef.current = { x: e.clientX, sidebarW };
    const onMove = (mv) => {
      const rawDx = mv.clientX - dragStartRef.current.x;
      const dx = i18n.language === 'he' ? -rawDx : rawDx;
      setSidebarW(Math.max(280, Math.min(560, dragStartRef.current.sidebarW + dx)));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const css = `
    .ldp-action-btn { transition: background 0.12s, border-color 0.12s; }
    .ldp-action-btn:hover { background: ${T.surface} !important; }
    .ldp-detail-action { transition: background 0.12s, border-color 0.12s; }
    .ldp-detail-action:hover { filter: brightness(1.1); }
    .ldp-post-tile { transition: background 0.12s; }
    .ldp-post-tile:hover { background: ${T.surface} !important; }
    .ldp-left-transition { animation: ldpSlideIn 0.2s ease-out; }
    @keyframes ldpSlideIn { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
    .ldp-right-transition { animation: ldpFadeSlideIn 0.2s ease-out; }
    @keyframes ldpFadeSlideIn { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
    .ldp-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; position: relative; z-index: 10; transition: background 0.15s; }
    .ldp-divider:hover, .ldp-divider:active { background: ${T.primary}55; }
    .ldp-divider::after { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 1px; height: 32px; background: ${T.border}; border-radius: 1px; }
  `;

  useEffect(() => {
    if (showSettings) {
      setLeftView('settings');
    }
  }, [showSettings]);

  useEffect(() => {
    const fetchUserLanguage = async () => {
      try {
        const language = await ListsManager.getListenerLanguage();
        setUserLanguage(language);
      } catch (err) {
        console.error('Error fetching user language:', err);
      }
    };
    fetchUserLanguage();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const searchSkipRef = useRef(true);
  useEffect(() => {
    if (searchSkipRef.current) {
      searchSkipRef.current = false;
      return;
    }
    setSelectedPost(null);
    loadListDetails(true, 0);
  }, [debouncedSearch]); // eslint-disable-line

  useEffect(() => {
    setSelectedPost(null);
    setSearchTerm('');
    searchSkipRef.current = true;
    setDebouncedSearch('');
    loadListDetails(true, 0);
  }, [listId]); // eslint-disable-line

  const showNewContentNotification = (count) => {
    setNewContentCount(count);
    setShowNewContentBubble(true);
    setTimeout(() => setShowNewContentBubble(false), 5000);
  };

  const loadListDetails = async (isInitialLoad = true, pageNum = 0) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      if (isInitialLoad) {
        setLoading(true);
        setError(null);
        setPage(0);
        setTweets([]);
        setTotalPosts(0);

        const lists = await ListsManager.fetchLists(activeOrg?.id);
        const details = lists.find(list => list.id === listId);
        if (!details) throw new Error('List not found');
        setListDetails(details);
      } else {
        setLoadingMore(true);
      }

      const offset = pageNum * ITEMS_PER_PAGE;
      const filters = activeFiltersRef.current;
      const since = filters.fromDate ? new Date(filters.fromDate)
        : refreshing ? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) : null;

      const searchParam = debouncedSearch.length >= 3 ? debouncedSearch : null;
      const activeSources = filters.sources || [];

      const SOURCE_FETCH_MAP = {
        [MessageSource.TWITTER]: () => ListsManager.fetchRelevantTweetsFromList(listId, since, ITEMS_PER_PAGE, offset, searchParam),
        [MessageSource.TELEGRAM]: () => ListsManager.getTelegramMessages(listId, since ? since.toISOString() : null, ITEMS_PER_PAGE, offset, searchParam),
        [MessageSource.TIKTOK]: () => ListsManager.fetchRelevantTiktoksFromList(listId, since, ITEMS_PER_PAGE, offset, searchParam),
        [MessageSource.INSTAGRAM]: () => ListsManager.fetchRelevantInstagramsFromList(listId, since, ITEMS_PER_PAGE, offset, searchParam),
        [MessageSource.FACEBOOK_GROUP]: () => ListsManager.fetchFacebookGroupPostsFromList(listId, since, ITEMS_PER_PAGE, offset, searchParam),
        'rss': () => ListsManager.fetchRelevantRssItemsFromList(listId, null, ITEMS_PER_PAGE, offset, searchParam),
        [MessageSource.WEBZ]: () => ListsManager.fetchRelevantWebzArticlesFromList(listId, null, ITEMS_PER_PAGE, offset, searchParam),
        [MessageSource.TV]: () => ListsManager.fetchRelevantTvSegmentsFromList(listId, null, ITEMS_PER_PAGE, offset, searchParam),
      };

      const fetches = Object.entries(SOURCE_FETCH_MAP)
        .filter(([key]) => activeSources.includes(key))
        .map(([, fn]) => fn());

      let resolvedCount = 0;
      let totalItems = 0;
      let totalCountSum = 0;   // sum of server-side per-source totals (all matching rows)
      const isRefreshing = refreshing;

      if (fetches.length === 0) {
        setLoading(false);
        setLoadingMore(false);
        setRefreshing(false);
        isFetchingRef.current = false;
        setHasMore(false);
        return;
      }

      fetches.forEach(promise => {
        promise
          .then(rawItems => {
            if (typeof rawItems?.totalCount === 'number') totalCountSum += rawItems.totalCount;
            let items = rawItems;
            if (items?.length && filters.selectedUsers?.length) {
              items = items.filter(it => filters.selectedUsers.includes(it.username));
            }
            if (items?.length && filters.toDate) {
              const toDateTs = new Date(filters.toDate).getTime();
              items = items.filter(it => !it.created_at || new Date(it.created_at).getTime() <= toDateTs);
            }
            if (items?.length) {
              totalItems += items.length;
              if (isRefreshing && !isInitialLoad) {
                setTweets(prev => {
                  const existingIds = new Set(prev.map(t => t.tweet_id || t.id));
                  const newItems = items.filter(msg => !existingIds.has(msg.tweet_id || msg.id));
                  if (newItems.length > 0) {
                    showNewContentNotification(newItems.length);
                    return [...newItems, ...prev];
                  }
                  return prev;
                });
              } else {
                setTweets(prev => {
                  const merged = [...prev, ...items];
                  merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                  return merged;
                });
              }
              if (isInitialLoad) setLoading(false);
            }
          })
          .catch(err => console.error('Source fetch failed:', err))
          .finally(() => {
            resolvedCount++;
            if (resolvedCount === fetches.length) {
              if (!isRefreshing && !isInitialLoad) setPage(pageNum);
              // Refresh fetches with a `since` filter (recent only), so its total is
              // partial — only update the full total on a normal load (initial / load-more).
              if (!isRefreshing) setTotalPosts(totalCountSum);
              setHasMore(totalItems >= ITEMS_PER_PAGE);
              setLoading(false);
              setLoadingMore(false);
              setRefreshing(false);
              isFetchingRef.current = false;
            }
          });
      });
    } catch (err) {
      console.error('Error loading list details:', err);
      setError('Failed to load list details');
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
      isFetchingRef.current = false;
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadListDetails(true, 0);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore && !isFetchingRef.current) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadListDetails(false, nextPage);
    }
  };

  const handleScroll = (event) => {
    const target = event.target;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 300
      && !loadingMore && hasMore && !isFetchingRef.current) {
      handleLoadMore();
    }
  };


  const handleFilterApply = (filterState) => {
    setCurrentSelectedSources(filterState.sources || []);
    setCurrentFromDate(filterState.fromDate ? new Date(filterState.fromDate) : null);
    setCurrentToDate(filterState.toDate ? new Date(filterState.toDate) : null);
    setCurrentSelectedUsers(filterState.selectedUsers);
    activeFiltersRef.current = {
      sources: filterState.sources || [],
      fromDate: filterState.fromDate,
      toDate: filterState.toDate,
      selectedUsers: filterState.selectedUsers,
    };
    setSelectedPost(null);
    loadListDetails(true, 0);
  };

  const activeFilterCount = (() => {
    const allSrcCount = 8;
    let count = 0;
    if (currentSelectedSources.length < allSrcCount) count++;
    if (currentFromDate) count++;
    if (currentSelectedUsers?.length > 0) count++;
    return count;
  })();

  const handleSubScreenClose = (result) => {
    setRightView('empty');
    if (result === true) {
      loadListDetails(true, 0);
      onListUpdated?.({ id: listId });
    } else if (result && result.refreshSettings) {
      const { notificationMode, notificationFrequency } = result;
      if (listDetails) {
        setListDetails({ ...listDetails, notification_mode: notificationMode, notification_frequency: notificationFrequency });
      }
    }
  };

  const handleDraftReaction = async (tweet, reactionListId) => {
    setCurrentReactionTweet(tweet);
    setCurrentReactionListId(reactionListId);

    const hasProfile = await ListsManager.hasListenerProfile();
    if (!hasProfile) { navigate('/writing-style'); return; }

    setShowReactionModal(true);
    setReactionLoading(true);
    setReactionError(null);
    setReactionText('');
    setReactionExplanation('');

    try {
      const result = await ListsManager.generateTweetReaction(
        tweet.tweet_content || tweet.translated_content,
        tweet.username || tweet.author_name || 'unknown',
        tweet.list_name, null, reactionListId
      );
      if (result && result.success) {
        setReactionText(result.reaction_text);
        setReactionExplanation(result.explanation || '');
      } else {
        setReactionError(result?.error_message || 'Failed to generate reaction.');
      }
    } catch (err) {
      setReactionError(err.message || 'Failed to generate reaction');
    } finally {
      setReactionLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError('');
    try {
      const success = await ListsManager.deleteList(listId);
      if (success) onListDeleted(listId);
      else setDeleteError('Failed to delete list.');
    } catch (err) {
      setDeleteError('An error occurred while deleting the list');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferTargetOrgId) return;
    setIsTransferring(true);
    setTransferError('');
    try {
      const result = await ListsManager.transferListToOrg(listId, transferTargetOrgId);
      if (result.success) {
        setShowTransferModal(false);
        onListDeleted(listId);
      } else {
        setTransferError(result.error || 'Transfer failed.');
      }
    } catch (err) {
      setTransferError('An error occurred during transfer.');
    } finally {
      setIsTransferring(false);
    }
  };


  const handleExportSelection = () => {
    const messageContent = selectedTweets.map(message => {
      const source = message.source === MessageSource.TELEGRAM ? 'Telegram' : 'Twitter';
      const authorName = message.author_name || message.username || 'Unknown';
      const content = message.translated_content || message.tweet_content || message.content || '';
      return `${source} message by ${authorName}:\n${content}\n\nURL: ${message.url || 'No URL'}\n\n`;
    }).join('---\n\n');

    const exportText = `Exported Messages from List: ${listDetails?.name || listName}\n\n${messageContent}`;
    const element = document.createElement('a');
    const file = new Blob([exportText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `reactfirst-list-${listId}-export.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleRemovePost = (post) => {
    setTweets(prev => prev.filter(t => (t.tweet_id || t.id) !== (post.tweet_id || post.id)));
    if (selectedPost && (selectedPost.tweet_id || selectedPost.id) === (post.tweet_id || post.id)) {
      setSelectedPost(null);
    }
  };

  // Loading is now inline in posts sidebar — no early return

  // ── Source counts ─────────────────────────────────────────────────────────
  const sourceCounts = {};
  tweets.forEach(t => {
    const s = t.source || 'unknown';
    sourceCounts[s] = (sourceCounts[s] || 0) + 1;
  });

  // ── Posts Sidebar ─────────────────────────────────────────────────────────
  const postsSidebar = (
    <div style={{
      width: sidebarW, flexShrink: 0,
      borderRight: `1px solid ${T.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 16px 12px', borderBottom: `1px solid ${T.border}`,
        flexShrink: 0,
      }}>
        {/* Top row: back + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ color: T.text, fontSize: 16, fontWeight: 700 }}>
            {listDetails?.name || listName || t('app.lists.posts') || 'Posts'}
          </span>
          <div style={{ flex: 1 }} />
          <div
            onClick={() => setLeftView(leftView === 'settings' ? 'posts' : 'settings')}
            style={{
              cursor: 'pointer', width: 26, height: 26, borderRadius: 6,
              background: leftView === 'settings' ? `${T.primary}15` : T.surface,
              border: `1px solid ${leftView === 'settings' ? `${T.primary}40` : T.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <FaCog size={10} color={leftView === 'settings' ? T.primary : T.textMuted} />
          </div>
        </div>

        {/* Search bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', borderRadius: 12,
          background: T.bg,
          marginBottom: 10,
        }}>
          <FaSearch size={14} color={T.textMuted} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('app.lists.searchPosts') || 'Search posts...'}
            style={{
              border: 'none', outline: 'none', background: 'transparent',
              color: T.text, fontSize: 14, flex: 1, fontWeight: 500,
            }}
          />
          {searchTerm && (
            <FaTimes
              size={11}
              color={T.textMuted}
              style={{ cursor: 'pointer' }}
              onClick={() => setSearchTerm('')}
            />
          )}
        </div>

        {/* Sort dropdown + Filter row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
              className="ldp-action-btn"
              style={{
                cursor: 'pointer', padding: '5px 10px', borderRadius: 6,
                border: `1px solid ${T.border}`,
                background: T.surface,
                display: 'flex', alignItems: 'center', gap: 5,
                color: T.textSec, fontSize: 11, fontWeight: 600,
              }}
            >
              <FaSortAmountDown size={9} />
              <span>{SORT_OPTIONS.find(o => o.key === sortKey)?.label}</span>
              {sortDir === 'asc' ? <FaArrowUp size={8} /> : <FaArrowDown size={8} />}
            </div>
            {sortDropdownOpen && (
              <>
                <div onClick={() => setSortDropdownOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
                <div style={{
                  position: 'absolute', top: '100%', marginTop: 4,
                  ...(i18n.language === 'he' ? { right: 0 } : { left: 0 }),
                  background: T.card, border: `1px solid ${T.border}`,
                  borderRadius: 10, padding: '6px 0', zIndex: 100,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                  minWidth: 170, direction: 'ltr',
                }}>
                  {SORT_OPTIONS.map(opt => {
                    const isActive = sortKey === opt.key;
                    return (
                      <div key={opt.key} style={{ padding: '2px 6px' }}>
                        <div style={{
                          padding: '6px 10px', color: T.textMuted, fontSize: 10, fontWeight: 700,
                          letterSpacing: 0.5, textTransform: 'uppercase',
                        }}>
                          {opt.label}
                        </div>
                        <div style={{ display: 'flex', gap: 4, padding: '0 6px 6px' }}>
                          <div
                            onClick={() => { setSortKey(opt.key); setSortDir('desc'); setSortDropdownOpen(false); }}
                            style={{
                              flex: 1, padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center',
                              background: isActive && sortDir === 'desc' ? `${T.primary}18` : T.surface,
                              border: `1px solid ${isActive && sortDir === 'desc' ? T.primary : T.border}`,
                              color: isActive && sortDir === 'desc' ? T.primary : T.textSec,
                              fontSize: 11, fontWeight: 600,
                            }}
                          >
                            <FaArrowDown size={8} /> {t('app.lists.sortDesc')}
                          </div>
                          <div
                            onClick={() => { setSortKey(opt.key); setSortDir('asc'); setSortDropdownOpen(false); }}
                            style={{
                              flex: 1, padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center',
                              background: isActive && sortDir === 'asc' ? `${T.primary}18` : T.surface,
                              border: `1px solid ${isActive && sortDir === 'asc' ? T.primary : T.border}`,
                              color: isActive && sortDir === 'asc' ? T.primary : T.textSec,
                              fontSize: 11, fontWeight: 600,
                            }}
                          >
                            <FaArrowUp size={8} /> {t('app.lists.sortAsc')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div
            ref={filterBtnRef}
            onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
            className="ldp-action-btn"
            style={{
              cursor: 'pointer', padding: '5px 10px', borderRadius: 6,
              border: `1px solid ${activeFilterCount > 0 ? T.primary : T.border}`,
              background: activeFilterCount > 0 ? `${T.primary}12` : T.surface,
              display: 'flex', alignItems: 'center', gap: 4,
              color: activeFilterCount > 0 ? T.primary : T.textSec,
              fontSize: 11, fontWeight: 600,
            }}
          >
            <FaFilter size={8} /> {t('app.lists.filter') || 'Filters'}
            {activeFilterCount > 0 && (
              <span style={{
                background: T.primary, color: '#fff', fontSize: 9, fontWeight: 700,
                width: 15, height: 15, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{activeFilterCount}</span>
            )}
          </div>
        </div>

        {/* Post count + refresh */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: T.textMuted, fontSize: 11 }}>
            {totalPosts > 0 ? `${totalPosts.toLocaleString()} ${t('app.lists.posts') || 'posts'}` : (tweets.length > 0 ? `${tweets.length.toLocaleString()} ${t('app.lists.posts') || 'posts'}` : '')}
          </span>
          <div style={{ flex: 1 }} />
          <div
            onClick={handleRefresh}
            style={{
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              color: T.primary, fontSize: 11, fontWeight: 600,
            }}
          >
            {refreshing ? <ActivityIndicator size="small" color={T.primary} /> : <FaSync size={9} />}
          </div>
        </div>
      </div>

      {/* Error bar */}
      {(error || deleteError) && (
        <div style={{
          padding: '8px 16px', flexShrink: 0,
          background: 'rgba(255,69,58,0.06)', borderBottom: '1px solid rgba(255,69,58,0.15)',
          color: T.error, fontSize: 12,
        }}>
          {error || deleteError}
        </div>
      )}

      {/* New content bubble */}
      {showNewContentBubble && (
        <div style={{ padding: '8px 12px', flexShrink: 0 }}>
          <div
            onClick={() => setShowNewContentBubble(false)}
            style={{
              padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
              background: `linear-gradient(135deg, ${T.primary}, #3b82f6)`,
              boxShadow: '0 4px 16px rgba(77,166,255,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>
              {t('app.lists.newMessages', { count: newContentCount })}
            </span>
          </div>
        </div>
      )}

      {/* Scrollable post grid */}
      <div
        style={{ flex: 1, overflowY: 'auto', padding: '10px 12px 20px' }}
        onScroll={handleScroll}
      >
        {loading && tweets.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', paddingTop: 48, gap: 12 }}>
            <ActivityIndicator size="small" color={T.primary} />
            <span style={{ color: T.textMuted, fontSize: 12 }}>{t('app.lists.loadingList')}</span>
          </div>
        ) : tweets.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', paddingTop: 48, gap: 10 }}>
            <FaCommentAlt size={24} color={T.textMuted} />
            <span style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>
              {debouncedSearch ? (t('app.lists.noSearchResults') || 'No results found') : (t('app.lists.noPosts') || 'No posts')}
            </span>
            {debouncedSearch && (
              <span
                style={{ color: T.primary, fontSize: 12, cursor: 'pointer' }}
                onClick={() => setSearchTerm('')}
              >
                {t('app.lists.clearSearch') || 'Clear search'}
              </span>
            )}
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}>
            {applySort(tweets).map((item, index) => (
              <PostTile
                key={`${item.tweet_id || item.id}-${index}`}
                item={{ ...item, list_name: listDetails?.name || listName }}
                listId={listId}
                listName={listDetails?.name || listName}
                onReaction={handleDraftReaction}
                isSelected={selectedPost && (selectedPost.tweet_id || selectedPost.id) === (item.tweet_id || item.id)}
                onSelect={(post) => { setSelectedPost(post); setRightView('post'); }}
                highlightText={debouncedSearch.length >= 3 ? debouncedSearch : ''}
              />
            ))}
          </div>
        )}

        {loadingMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16, marginTop: 8 }}>
            <ActivityIndicator size="small" color={T.primary} />
          </div>
        )}

        {!hasMore && tweets.length > 0 && (
          <div style={{ textAlign: 'center', color: T.textMuted, padding: 14, fontSize: 11,
            borderTop: `1px solid ${T.border}`, marginTop: 10 }}>
            {t('app.lists.endOfList')}
          </div>
        )}
      </div>
    </div>
  );

  // ── Settings Panel Content ────────────────────────────────────────────────
  const settingsContent = (
    <div style={{ padding: '20px 16px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* List identity */}
      <div style={{
        padding: '20px 20px 16px',
        borderRadius: 16, border: `1px solid ${T.border}`,
        background: T.card,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 16, flexShrink: 0,
            background: 'rgba(77,166,255,0.12)', border: '1px solid rgba(77,166,255,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: T.primary, fontSize: 22, fontWeight: 800 }}>
              {(listDetails?.name || listName || '?')[0].toUpperCase()}
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: T.text, fontSize: 20, fontWeight: 700, lineHeight: '26px', letterSpacing: -0.3 }}>
              {listDetails?.name || listName}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: 3, background: '#30d158' }} />
              <span style={{ color: '#30d158', fontSize: 10, fontWeight: 700, letterSpacing: 0.8 }}>{t('app.lists.active')}</span>
              {listDetails?.list_role && (
                <>
                  <span style={{ color: T.textMuted, fontSize: 10 }}>·</span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                    color: { brand: '#0a84ff', competitor: '#ff453a', general: '#8e8e93' }[listDetails.list_role] || '#8e8e93',
                  }}>
                    {({ brand: t('app.search.roleBrand') || 'Brand', competitor: t('app.search.roleCompetitor') || 'Competitor', general: t('app.search.roleGeneral') || 'General' })[listDetails.list_role] || listDetails.list_role}
                  </span>
                </>
              )}
              {tweets.length > 0 && (
                <>
                  <span style={{ color: T.textMuted, fontSize: 10 }}>·</span>
                  <FaCommentAlt size={9} color={T.textMuted} />
                  <span style={{ color: T.textMuted, fontSize: 10 }}>{(totalPosts > 0 ? totalPosts : tweets.length).toLocaleString()} posts</span>
                </>
              )}
            </div>
          </div>
        </div>

        {listDetails?.request && (
          <div style={{
            padding: '12px 14px', borderRadius: 10,
            background: T.surface, border: `1px solid ${T.border}`,
          }}>
            <div style={{ color: T.textMuted, fontSize: 9, fontWeight: 700,
              letterSpacing: 0.8, marginBottom: 6 }}>
              {t('app.lists.monitoringPrompt')}
            </div>
            <div style={{ color: T.textSec, fontSize: 13, lineHeight: '19px' }}>
              {listDetails.request}
            </div>
          </div>
        )}
      </div>

      {/* Source breakdown */}
      {tweets.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {Object.entries(sourceCounts).map(([source, count]) => {
            const PIcon = PLATFORM_ICONS[source] || FaGlobe;
            const color = PLATFORM_COLORS[source] || '#8e8e93';
            return (
              <div key={source} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 10px', borderRadius: 20,
                background: `${color}12`, border: `1px solid ${color}30`,
              }}>
                <PIcon size={10} color={color} />
                <span style={{ color, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {source}
                </span>
                <span style={{ color: T.textMuted, fontSize: 10 }}>{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div>
        <div style={{ color: T.textMuted, fontSize: 9, fontWeight: 700,
          letterSpacing: 0.8, marginBottom: 10 }}>
          {t('app.lists.actionsLabel')}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <SettingsActionBtn icon={FaUsers}  label={t('app.lists.users')}  onClick={() => setRightView('users')} />
          <SettingsActionBtn icon={FaBell}   label={t('app.lists.notify')} onClick={() => setRightView('notifications')} />
          <SettingsActionBtn icon={FaEdit}   label={t('app.lists.edit')}   onClick={() => setRightView('edit')} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <SettingsActionBtn
            icon={isDeleting ? (() => <ActivityIndicator size="small" color="#f43f5e" />) : FaTrash}
            label={isDeleting ? t('app.lists.deleting') : t('app.lists.delete')}
            onClick={handleDelete}
            danger
          />
          {orgs.length > 1 && (
            <SettingsActionBtn
              icon={FaExchangeAlt}
              label={t('app.lists.transfer')}
              onClick={() => {
                setTransferTargetOrgId('');
                setTransferError('');
                setShowTransferModal(true);
              }}
            />
          )}
          {selectedTweets.length > 0 && (
            <>
              <SettingsActionBtn icon={FaDownload} label={`${t('app.lists.export')} (${selectedTweets.length})`} onClick={handleExportSelection} />
              <SettingsActionBtn icon={FaTimes} label={t('app.lists.clear')} onClick={() => setSelectedTweets([])} />
            </>
          )}
        </div>
      </div>

      {/* Settings flags */}
      {listDetails && (
        <div style={{
          padding: '14px 16px', borderRadius: 12,
          background: T.surface, border: `1px solid ${T.border}`,
        }}>
          <div style={{ color: T.textMuted, fontSize: 9, fontWeight: 700,
            letterSpacing: 0.8, marginBottom: 10 }}>
            {t('app.lists.settingsLabel')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {[
              { label: t('app.lists.listRole') || 'Role', value: (() => {
                const role = listDetails.list_role || 'general';
                const labels = { brand: t('app.search.roleBrand') || 'Brand', competitor: t('app.search.roleCompetitor') || 'Competitor', general: t('app.search.roleGeneral') || 'General' };
                return labels[role] || role;
              })(), color: { brand: '#0a84ff', competitor: '#ff453a', general: '#8e8e93' }[listDetails.list_role || 'general'] },
              { label: t('app.lists.visibility'), value: (listDetails.visibility || 'org').toUpperCase() },
              { label: t('app.lists.wideMonitoringTwitter'), value: listDetails.is_wide_monitoring ? t('app.lists.on') : t('app.lists.off'),
                color: listDetails.is_wide_monitoring ? '#30d158' : T.textMuted },
              { label: t('app.lists.wideMonitoringTiktok'), value: listDetails.is_wide_monitoring_tiktok ? t('app.lists.on') : t('app.lists.off'),
                color: listDetails.is_wide_monitoring_tiktok ? '#30d158' : T.textMuted },
              { label: t('app.lists.wideMonitoringInstagram'), value: listDetails.is_wide_monitoring_instagram ? t('app.lists.on') : t('app.lists.off'),
                color: listDetails.is_wide_monitoring_instagram ? '#30d158' : T.textMuted },
              { label: 'Wide Monitoring: FB Groups', value: listDetails.is_wide_monitoring_fb_groups ? t('app.lists.on') : t('app.lists.off'),
                color: listDetails.is_wide_monitoring_fb_groups ? '#30d158' : T.textMuted },
              { label: t('app.lists.notifications'), value: (listDetails.notification_mode || 'MUTE').toUpperCase() },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: T.textMuted, fontSize: 11 }}>{row.label}</span>
                <span style={{ color: row.color || T.textSec, fontSize: 11, fontWeight: 600 }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const settingsSidebar = (
    <div className="ldp-left-transition" style={{
      width: sidebarW, flexShrink: 0,
      borderRight: `1px solid ${T.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%',
    }}>
      <div style={{
        padding: '14px 16px', borderBottom: `1px solid ${T.border}`,
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div
          onClick={() => { setLeftView('posts'); onCloseSettings?.(); }}
          style={{
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
            color: T.primary, fontSize: 13, fontWeight: 600,
          }}
        >
          <FaArrowLeft size={11} color={T.primary} />
          {t('app.lists.posts') || 'Posts'}
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>
          {t('app.lists.settingsLabel') || 'Settings'}
        </span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {settingsContent}
      </div>
    </div>
  );

  const renderRightColumn = () => {
    if (rightView === 'post' && selectedPost) {
      return (
        <div key="post" className="ldp-right-transition" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PostDetailView
            post={selectedPost}
            listId={listId}
            listName={listDetails?.name || listName}
            onClose={() => { setSelectedPost(null); setRightView('empty'); }}
            onDraftReaction={handleDraftReaction}
            onRemovePost={handleRemovePost}
            t={t}
            highlightText={debouncedSearch.length >= 3 ? debouncedSearch : ''}
          />
        </div>
      );
    }
    if (rightView === 'users') {
      return (
        <div key="users" className="ldp-right-transition" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div onClick={() => handleSubScreenClose()} style={{ cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaTimes size={12} color={T.textMuted} />
            </div>
            <span style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>{listDetails?.name || listName} — {t('app.lists.users')}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <ListUsersScreen listId={listId} initialListName={listDetails?.name || listName}
              onClose={() => handleSubScreenClose()} isModal={true} />
          </div>
        </div>
      );
    }
    if (rightView === 'notifications') {
      return (
        <div key="notifications" className="ldp-right-transition" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div onClick={() => handleSubScreenClose()} style={{ cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaTimes size={12} color={T.textMuted} />
            </div>
            <span style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>{t('app.lists.notificationSettings')}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <NotificationSettingsScreen
              listId={listId} listName={listDetails?.name || listName}
              notificationMode={listDetails?.notification_mode?.toLowerCase() || 'mute'}
              notificationFrequency={listDetails?.notification_frequency}
              onClose={(result) => handleSubScreenClose(result)} isModal={true} />
          </div>
        </div>
      );
    }
    if (rightView === 'edit') {
      return (
        <div key="edit" className="ldp-right-transition" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div onClick={() => handleSubScreenClose()} style={{ cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaTimes size={12} color={T.textMuted} />
            </div>
            <span style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>{t('app.lists.editList')}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <NewListScreen
              isForEditing={true} listId={listId}
              existingListName={listDetails?.name || listName}
              existingListPrompt={listDetails?.request || ''}
              existingListReactionStyle={listDetails?.reaction_writing_style || ''}
              existingIsWideMonitoring={listDetails?.is_wide_monitoring || false}
              existingIsWideMonitoringTiktok={listDetails?.is_wide_monitoring_tiktok || false}
              existingIsWideMonitoringInstagram={listDetails?.is_wide_monitoring_instagram || false}
              existingVisibility={listDetails?.visibility || 'org'}
              existingListRole={listDetails?.list_role || 'general'}
              onClose={(result) => handleSubScreenClose(result)} isModal={true} />
          </div>
        </div>
      );
    }
    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 14,
        background: T.bg,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 16,
          background: 'rgba(77,166,255,0.06)',
          border: '1px solid rgba(77,166,255,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <FaCommentAlt size={22} color={T.textMuted} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: T.text, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            {t('app.lists.selectPostTitle') || 'Select a post'}
          </div>
          <div style={{ color: T.textMuted, fontSize: 12 }}>
            {t('app.lists.selectPostDesc') || 'Choose a post from the list to view details'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: T.bg }}>
        {leftView === 'settings' ? settingsSidebar : postsSidebar}
        <div className="ldp-divider" onMouseDown={startDrag} />
        {renderRightColumn()}
      </div>

      <FilterDropdown
        isOpen={filterDropdownOpen}
        onClose={() => setFilterDropdownOpen(false)}
        triggerRef={filterBtnRef}
        tweets={tweets}
        sources={currentSelectedSources}
        fromDate={currentFromDate?.toISOString() || ''}
        toDate={currentToDate?.toISOString() || ''}
        selectedUsers={currentSelectedUsers || []}
        onApply={handleFilterApply}
      />

      <ReactionModal
        isVisible={showReactionModal} onClose={() => setShowReactionModal(false)}
        reactionText={reactionText} explanation={reactionExplanation}
        isLoading={reactionLoading}
        onRegenerate={() => currentReactionTweet && handleDraftReaction(currentReactionTweet, currentReactionListId)}
        platform={currentReactionTweet?.source === MessageSource.TELEGRAM ? 'telegram' : 'twitter'}
        originalAuthor={currentReactionTweet?.username || currentReactionTweet?.author_name}
        tweetId={currentReactionTweet?.tweet_id || currentReactionTweet?.id}
        error={reactionError}
      />

      <Modal isVisible={showTransferModal} onClose={() => setShowTransferModal(false)} title={t('app.lists.transferListToOrg')}>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ color: T.textSec, fontSize: 13, lineHeight: '19px' }}>
            {t('app.lists.transferDesc', { name: listDetails?.name || listName })}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.8 }}>
              {t('app.lists.destinationOrg')}
            </label>
            <select
              value={transferTargetOrgId}
              onChange={e => setTransferTargetOrgId(e.target.value)}
              style={{
                background: T.surface, border: `1px solid ${T.border}`,
                borderRadius: 8, padding: '9px 12px',
                color: T.text, fontSize: 13, outline: 'none', cursor: 'pointer',
              }}
            >
              <option value="">{t('app.lists.selectOrg')}</option>
              {orgs
                .filter(o => o.id !== listDetails?.org_id && o.id !== activeOrg?.id)
                .map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
            </select>
          </div>

          {transferError && (
            <div style={{
              padding: '10px 14px', borderRadius: 8,
              background: 'rgba(255,69,58,0.08)', border: '1px solid rgba(255,69,58,0.2)',
              color: T.error, fontSize: 12,
            }}>
              {transferError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowTransferModal(false)}
              style={{
                padding: '8px 18px', borderRadius: 8, border: `1px solid ${T.border}`,
                background: 'transparent', color: T.textSec, fontSize: 13, cursor: 'pointer',
              }}
            >
              {t('app.lists.cancel')}
            </button>
            <button
              onClick={handleTransfer}
              disabled={!transferTargetOrgId || isTransferring}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none',
                background: transferTargetOrgId && !isTransferring ? T.primary : `${T.primary}55`,
                color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: transferTargetOrgId && !isTransferring ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {isTransferring ? <ActivityIndicator size="small" color="#fff" /> : <FaExchangeAlt size={12} />}
              {isTransferring ? t('app.lists.transferring') : t('app.lists.transfer')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export { PostDetailView };
export default ListDetailPanel;
